import { useApi } from '@classgo/app-kit';
import type { StudentStatus } from '@classgo/shared';
import { changeStudentStatusInputSchema } from '@classgo/shared';
import { Input, Select } from '@classgo/ui';

import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { peopleApi } from '../people/api';
import { FormShell } from './form-shell';

/**
 * A dated status transition: withdrawal, graduation or reactivation, with the reason.
 */
export const StatusForm = ({
  studentId,
  current,
  onDone,
}: {
  studentId: string;
  current: string;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const form = useForm({
    schema: changeStudentStatusInputSchema,
    initial: {
      status: (current === 'active' ? 'withdrawn' : 'active') as StudentStatus,
      effectiveOn: new Date().toISOString().slice(0, 10),
      reason: '',
    },
    toInput: (values) => ({
      status: values.status,
      effectiveOn: values.effectiveOn,
      reason: values.reason.trim() === '' ? undefined : values.reason.trim(),
    }),
  });
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      submitLabel={t('student.status.confirm')}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            await peopleApi(api).changeStatus(studentId, input);
          })
          .then((ok) => ok && onDone());
      }}
    >
      <Select
        label={t('student.field.status')}
        value={form.values.status}
        onChange={(event) => form.set('status', event.target.value as StudentStatus)}
        options={[
          { value: 'withdrawn', label: t('student.status.withdrawn') },
          { value: 'graduated', label: t('student.status.graduated') },
          { value: 'active', label: t('student.status.active') },
        ]}
      />
      <Input
        label={t('student.status.effectiveOn')}
        type="date"
        required
        value={form.values.effectiveOn}
        onChange={(event) => form.set('effectiveOn', event.target.value)}
        {...(form.fieldError('effectiveOn') ? { error: form.fieldError('effectiveOn') } : {})}
      />
      <Input
        label={t('student.status.reason')}
        value={form.values.reason}
        onChange={(event) => form.set('reason', event.target.value)}
      />
      <p className="text-sm text-gray-500">{t('student.status.keepsRecords')}</p>
    </FormShell>
  );
};
